import { useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { ApiError, apiErrorFromResponse, toApiError } from '../utils/apiError';

/**
 * Error thrown by `request()` for any non-success response. `status` is the HTTP status,
 * `code` the backend ErrorCode when the body carried one.
 */
export const STAGE_REFUSAL_MESSAGE = 'Training opens after Risk Analysis (Stage 6) is approved.';

export { ApiError };

/**
 * Thin authenticated request layer. It attaches the MSAL bearer token and nothing else —
 * tenant and project scope come from the token and the `/projects/:id/...` path segment the
 * caller supplies; callers never send a tenantId (Commandment III, enforced server-side).
 *
 * `request(method, path, { body, query, formData, expect })`
 *   - `path` is relative to `/api/v1` (e.g. `/projects/p1/training/courses`).
 *   - resolves `{ data, meta }` from the standard `{ success, data, meta }` envelope.
 *   - `expect: 'blob'` resolves `{ blob, filename, headers }` (file downloads).
 *   - throws ApiError for HTTP failure or `success: false`.
 */
export function useApiRequest() {
    const { instance, accounts } = useMsal();
    // Latest MSAL handles live in a ref so `request` keeps ONE identity for the component's
    // lifetime. Screens key their `useResource` loaders on `request`; a per-render identity
    // (a fresh `accounts` array) would re-fire every load on every render.
    const msalRef = useRef({ instance, accounts });
    msalRef.current = { instance, accounts };

    const getApiToken = useCallback(async () => {
        const { instance: msal, accounts: accts } = msalRef.current;
        const tokenResponse = await msal.acquireTokenSilent({ ...apiTokenRequest, account: accts[0] });
        return tokenResponse.accessToken;
    }, []);

    const request = useCallback(
        async (method, path, { body, query, formData, expect } = {}) => {
            let token;
            try {
                token = await getApiToken();
            } catch (err) {
                throw toApiError(err);
            }
            const qs = query
                ? Object.entries(query)
                      .filter(([, v]) => v !== undefined && v !== null && v !== '')
                      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
                      .join('&')
                : '';
            const url = `${apiConfig.baseUrl}/api/v1${path}${qs ? `?${qs}` : ''}`;
            const headers = { Authorization: `Bearer ${token}` };
            let payload;
            if (formData) {
                payload = formData; // browser sets the multipart boundary itself
            } else if (body !== undefined) {
                headers['Content-Type'] = 'application/json';
                payload = JSON.stringify(body);
            }
            let res;
            try {
                res = await fetch(url, { method, headers, body: payload });
            } catch (err) {
                throw toApiError(err);
            }

            if (expect === 'blob' && res.ok) {
                const blob = await res.blob();
                const disposition = (res.headers && res.headers.get && res.headers.get('content-disposition')) || '';
                const match = /filename="?([^";]+)"?/i.exec(disposition);
                return { blob, filename: match ? match[1] : null, headers: res.headers };
            }

            const json = await res.json().catch(() => null);
            if (!res.ok || !json || json.success === false) {
                const apiErr = apiErrorFromResponse(res, json);
                // A Stage 7 gate refusal is shown as a plain sentence wherever it surfaces, never as a raw error.
                if (apiErr.details && apiErr.details.reason === 'STAGE_PREREQUISITE_NOT_MET') {
                    apiErr.message = STAGE_REFUSAL_MESSAGE;
                    apiErr.stageRefusal = true;
                }
                throw apiErr;
            }
            return { data: json.data, meta: json.meta };
        },
        [getApiToken],
    );

    return { request };
}
